<template>
  <TileWrapperComponent
    v-show="displayTile"
    :key="td.status"
    :is-loading="isLoading"
    :tile-data="td"
    notification-context="AllStateOverviewComponent"
  >
    <template #title>{{ tn(`${td.title}`) }}</template>
    <template #content>
      <TileDetailsComponent>
        <template v-if="showZeroState" #content>
          <p><svg-icon-component icon="logo-allstate" /></p>
          <p>{{ tn(`zeroStateP1`) }}</p>
          <p>
            <small>{{ tn(`zeroStateP2`) }}</small>
          </p>
          <button id="buyAllStateBtn" class="btn-outline-primary" type="button" @click="buyAllState()">
            {{ tn(`zeroStateBtn`) }}
          </button>
        </template>

        <template v-else-if="hasInactivePremium && !alreadyHasAccountSetup" #content>
          <p><svg-icon-component icon="logo-allstate" /></p>
          <p class="mb">{{ tn(`premiumCreateAccountP1`) }}</p>
          <div class="side-by-side">
            <button id="createAccountAllStateBtn" class="btn-primary no-shrink mr-half" type="button" disabled="true">
              {{ tn(`createAccountBtn`) }}
            </button>
            <small class="text-sm">
              {{ tn(`inactiveDisclaimer`) }}
            </small>
          </div>
        </template>

        <template v-else-if="(hasAddOn || hasActivePremium) && !alreadyHasAccountSetup" #content>
          <p>
            <span class="danger-text">
              <strong>{{ t(`HomeComponent.dangerDetails`) }}</strong>
            </span>
            {{ tn("attentionOnCreate") }}
          </p>
          <p><svg-icon-component icon="logo-allstate" /></p>
          <button id="createAccountAllStateBtn" class="btn-primary" type="button" @click="goToCreateAccountForm()">
            {{ tn(`createAccountBtn`) }}
          </button>
        </template>

        <template v-else-if="(hasAddOn || hasActivePremium) && alreadyHasAccountSetup" #content>
          <p><svg-icon-component icon="logo-allstate" /></p>
          <p class="mb">{{ tn(`manageAccountP1`) }}</p>
          <button id="manageAccountAllStateBtn" class="btn-outline-primary" type="button" @click="manageAccount()">
            {{ tn(`goToIDPBtn`) }}
          </button>
        </template>

        <template v-else-if="hasExpiredPremiumOrAddOn !== 'false'" #content>
          <p><svg-icon-component icon="logo-allstate" /></p>
          <p class="mb">
            {{ tn(`subscriptionExpired`, { subscription: hasExpiredPremiumOrAddOn }) }}
          </p>
          <button id="renewNowExpiredBtn" class="btn-link" type="button" @click="renewNow()">
            {{ tn(`renewYourSub`) }}
          </button>
        </template>
      </TileDetailsComponent>
    </template>
  </TileWrapperComponent>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { logEvent, logException } from "@/common/logger";
import TileDetailsComponent from "@/components/shared/TileDetails.vue";
import TileWrapperComponent from "@/components/shared/TileWrapper.vue";
import { ITileData } from "@/components/Home";
import { TileType, TileTitle, StatusType, StateType, KeyCodeSeller } from "@/globalEnums";
import { t } from "@/i18n";
import { IAllstateResponse } from "@/common/api/unifiedPortal/interfaces";
import { useSubscriptionsStore } from "@/stores/subscriptions";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { useRouter } from "vue-router";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { useUserStore } from "@/stores/user";
import { getProductDisplayNameBySku, IProductDisplayRequest } from "@/common/productsBySku";
import { SUBSCRIPTION_MIN_GRACE_PERIOD_DAY } from "@/define";
import { useAllstateSubscriptionsStore } from "@/stores/allstateSubscriptions";
import { getUrl } from "@/common/getUrl";
import { premiumSKUs, allstateSKUs, addOns } from "@/common/webrootProductName";
import { unifiedApi } from "@/common";
import { AxiosError } from "axios";
import { handleApiError } from "@/common/handleApiError";

const name = "AllStateOverviewComponent";

export default defineComponent({
  name,
  components: {
    TileWrapperComponent,
    TileDetailsComponent,
    SvgIconComponent,
  },
  emits: ["tile-ready"],
  setup(props, context) {
    logEvent("created", name);
    const router = useRouter();
    const textRoot = ref<string>("AllstateHomeComponent.");
    const isLoading = ref(true);
    const allstateSubscriptions = ref<IAllstateResponse[]>();
    const subscriptions = ref<ISubscription[]>([]);
    const subscriptionsStore = useSubscriptionsStore();
    const allstateSubscriptionsStore = useAllstateSubscriptionsStore();
    const userStore = useUserStore();

    const td = ref<ITileData>({
      title: TileTitle.allStateOverview,
      type: TileType.tile,
      status: StatusType.normal,
      state: StateType.notPurchased,
    });

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${name}.${v}`, params);
    }

    const hasActivePremium = computed(() => {
      const response =
        subscriptions.value &&
        subscriptions.value.some(
          l => premiumSKUs.includes(l.sku ?? "") && l.expirationDays > 0 && l.webrootStatusDescription === "Active"
        );
      return response;
    });

    const hasInactivePremium = computed(() => {
      const response =
        !hasActivePremium.value &&
        subscriptions.value &&
        subscriptions.value.some(l => premiumSKUs.includes(l.sku ?? "") && l.webrootStatusDescription === "Inactive");
      return response;
    });

    const hasAddOn = computed(() => {
      const response =
        subscriptions.value && subscriptions.value.some(l => addOns.includes(l.sku ?? "") && l.expirationDays > 0);
      return response;
    });

    const hasExpiredPremiumOrAddOn = computed(() => {
      let result = "false";
      if (subscriptions.value) {
        const allstateSubs = subscriptions.value.filter(s => allstateSKUs.includes(s.sku ?? ""));
        const hasExpired =
          allstateSubs.length > 0
            ? allstateSubs.every(l => l.expirationDays <= 0 && l.expirationDays >= SUBSCRIPTION_MIN_GRACE_PERIOD_DAY)
            : false;

        if (hasExpired) {
          const request: IProductDisplayRequest = {
            sku: allstateSubs[0].sku,
            webrootLicenseKey: allstateSubs[0].keyCode,
            isTrial: allstateSubs[0].isTrial,
          };
          result = t(getProductDisplayNameBySku(request));
        }
      }

      return result;
    });

    const alreadyHasAccountSetup = computed(() => {
      const response = allstateSubscriptions.value && allstateSubscriptions.value.some(a => a.canceledOnDate === null);
      return response;
    });

    const showZeroState = computed(() => {
      return (
        !hasAddOn.value &&
        !hasActivePremium.value &&
        !hasInactivePremium.value &&
        !alreadyHasAccountSetup.value &&
        hasExpiredPremiumOrAddOn.value === "false"
      );
    });

    function buyAllState() {
      if (subscriptions.value.some(w => w.keyCodeSellerType === KeyCodeSeller.Webroot)) {
        router.push("/allstateSignUp");
      } else {
        window.open(getUrl("WEBROOT_LEARN_MORE"));
      }
    }

    function goToCreateAccountForm() {
      router.push("allstateSignUp");
    }

    function manageAccount() {
      window.open(getUrl("ALLSTATE_MANAGE_URL"), "_blank");
    }

    onMounted(async () => {
      try {
        allstateSubscriptions.value = (
          await allstateSubscriptionsStore.getAllstateSubscriptions()
        ).allstateSubscriptions;
        subscriptions.value = await subscriptionsStore.getSubscriptions();

        // Add notification if expired
        td.value.status =
          hasExpiredPremiumOrAddOn.value !== "false" ||
          ((hasAddOn.value || hasActivePremium.value) && !alreadyHasAccountSetup.value)
            ? StatusType.danger
            : StatusType.normal;

        isLoading.value = false;
        context.emit("tile-ready");
      } catch (err) {
        logException(err as Error);
      }
    });

    async function renewNow() {
      try {
        if (hasExpiredPremiumOrAddOn.value) {
          const keycode = subscriptions.value.find(
            s =>
              allstateSKUs.includes(s.sku ?? "") &&
              s.expirationDays <= 0 &&
              s.expirationDays >= SUBSCRIPTION_MIN_GRACE_PERIOD_DAY
          )?.keyCode;
          if (keycode) {
            var url = (await unifiedApi.getWebrootRenewUrl(keycode)).data.redirectUrl;
            window.open(url, "_blank");
          }
        }
      } catch (err) {
        handleApiError(err as AxiosError, false);
      }
    }

    const displayTile = computed(() => userStore.selectedLanguage === "en");

    return {
      t,
      td,
      tn,
      textRoot,
      isLoading,
      buyAllState,
      hasActivePremium,
      hasInactivePremium,
      hasAddOn,
      alreadyHasAccountSetup,
      showZeroState,
      goToCreateAccountForm,
      manageAccount,
      displayTile,
      hasExpiredPremiumOrAddOn,
      renewNow,
    };
  },
});
</script>
